import {FormEvent, useState} from "react"
import CloseModalIcon from "@material-ui/icons/Close"
import type {LeadPageType} from "@/lib/server/models/leadPage"
import { useRouter } from "next/router"

export type LeadPageDisplayProps = {
    firstName: string
    lastName: string
    professionalTitle?: string
    realtyHouse?: string
    phone?: string
    completeAddress?: string
    pictureUrl?: string
    cityAndState?: string
    allowPopups?: boolean
    pageId: string
    type: LeadPageType | string & {}
    onehomesLink?: string | null
}

export function LeadPageDisplay(
    props: LeadPageDisplayProps
): JSX.Element {
    const {
        firstName = "John",
        lastName = "Smith",
        professionalTitle = "REALTOR®, Broker",
        realtyHouse = "eXp Realty",
        phone = "(910) 123-4567",
        completeAddress = "110 Nolancrest Manor NW, Calgary, AB",
        pictureUrl = "/favicon.png",
        cityAndState = "Calgary, Alberta",
        allowPopups = true,
    } = props

    const [showSearch, setShowSearch] = useState(false)
    const [showValution, setShowValuation] = useState(false)

    const theme = getPageTheme(props.type)

    const cleanedAddress = completeAddress
        .replaceAll(",", "")
        .trim()

    const router = useRouter()
    
    function onHomeWorth() {
        if (!props.onehomesLink) {
            setShowValuation(true)
            return
        }
        router.push(props.onehomesLink + "/cma")
    }

    return <div className="w-full h-full bg-gray-100 flex justify-center">
        
        {showSearch && allowPopups ? <PropertySearchModal
            onClose={() => setShowSearch(false)}
            pageId={props.pageId}
        /> : null}

        {showValution && allowPopups ? <ValutionModal 
            onClose={() => setShowValuation(false)}
            firstName={firstName}
            lastName={lastName}
            pageId={props.pageId}
        /> : null}
        
        <div 
            className="text-gray-900 text-center w-full z-0 py-8"
        >
            <div className="flex items-center justify-center">
                <img src={theme.iconUrl}
                    className="w-1/4"
                    style={{
                        minWidth: "150px",
                        maxWidth: "250px"
                    }}
                />
            </div>
            
            <div className={`text-lg mt-10 ${theme.topText === "blue" ? "text-blue-600" : "text-red-600"}`}>
                {`Serving the entire ${cityAndState} area...`}
            </div>

            <div className="mt-10">
                <div>
                    <button 
                        className={`text-xl font-light py-5 w-4/5 max-w-screen-sm border-4 mb-4 border-solid ${theme.propertySearchBtn === "blue" ? "text-blue-600 border-blue-600 hover:text-gray-100 hover:bg-blue-600" : "text-red-600 border-red-600 hover:text-gray-100 hover:bg-red-600"}`}
                        onClick={() => setShowSearch(true)}
                    >
                        {"Search all area properties now"}
                    </button>
                </div>

                <div>
                    <button 
                        className={`text-xl font-light py-5 w-4/5 max-w-screen-sm border-4 mb-4 border-solid ${theme.valuationBtn === "blue" ? "text-blue-600 border-blue-600 hover:text-gray-100 hover:bg-blue-600" : theme.valuationBtn === "green" ? "text-green-600 border-green-600  hover:text-gray-100 hover:bg-green-600" : "text-red-600 border-red-600 hover:text-gray-100 hover:bg-red-600"}`}
                        onClick={onHomeWorth}
                    >
                        {"What's my home worth?"}
                    </button>
                </div>
                
                <a href={allowPopups ? "tel:" + phone.replace(" ", "") : "#"}>
                    <div>
                        <button className={`text-xl font-light py-5 w-4/5 max-w-screen-sm mb-4 border-4 border-solid ${theme.callBtn === "blue" ? "text-blue-600 border-blue-600 hover:text-gray-100 hover:bg-blue-600" : "text-red-600 border-red-600 hover:text-gray-100 hover:bg-red-600"}`}>
                            {`Call ${firstName} to ask a question`}
                        </button>
                    </div>
                </a>
            </div>

            <div className=" mt-5 flex w-4/5 max-w-screen-sm items-center justify-center mx-auto">
                <div className="mr-5">
                    <img 
                        src={pictureUrl} 
                        alt="realtor picture"
                        height={"70px"}
                        width={"70px"}
                        className="rounded-full"
                    />
                </div>

                <div>
                    <div className="font-bold">
                        {`${firstName} ${lastName}, ${realtyHouse}`}
                    </div>
                    <div className="text-left">
                        {professionalTitle}
                    </div>
                </div>
            </div>

            <div className="mt-12 text-gray-700">
                {`${realtyHouse} • ${phone}${cleanedAddress.length < 1 ? "" : ` • ${completeAddress}`}`}
            </div>

            <div className="mt-3 text-gray-600">
                {`©${new Date().getFullYear()} All rights reserved.`}
            </div>
        </div>
    </div>
}

type ThemeColors = "blue" | "red"

type PageTheme = {
    iconUrl: string
    topText: ThemeColors
    propertySearchBtn: ThemeColors
    valuationBtn: ThemeColors | "green"
    callBtn: ThemeColors
}

const GOOGLE_MY_BUSINESS_ICON_URL = "/leadPages/google-rating.svg"
const FACEBOOK_ICON_URL = "/leadPages/facebook-rating.svg"
const YELP_ICON_URL = "/leadPages/yelp-rating.svg"
const ZILLOW_ICON_URL = "/leadPages/zillow-rating.svg"

function getPageTheme(type: LeadPageType | string & {}): PageTheme {
    let valuationBtn: PageTheme["valuationBtn"] = "blue"
    if (type === "googleMyBusiness") {
        valuationBtn = "green"
    } else if (type === "yelp") {
        valuationBtn = "red"
    }
    
    let callBtn: ThemeColors = "red"
    if (type === "zillow" || type === "facebook") {
        callBtn = "blue"
    }

    let iconUrl = ""
    switch (type) {
        case "facebook":
            iconUrl = FACEBOOK_ICON_URL
            break
        case "yelp":
            iconUrl = YELP_ICON_URL
            break
        case "zillow":
            iconUrl = ZILLOW_ICON_URL
            break
        case "googleMyBusiness":
        default:
            iconUrl = GOOGLE_MY_BUSINESS_ICON_URL
            break
    }

    return {
        iconUrl,
        topText: type === "yelp" ? "red" : "blue",
        propertySearchBtn: type === "yelp" ? "red" : "blue",
        valuationBtn,
        callBtn
    }
}

type PropertySearchModalProps = {
    onClose: () => unknown
    pageId: string
}

function PropertySearchModal(
    props: PropertySearchModalProps
): JSX.Element {

    const [gettingRedirect, setGettingRedirect] = useState(false)
    
    async function sendSearchPropertyData(
        event: FormEvent<HTMLFormElement>
    ) {
        event.preventDefault()
        const data = new FormData(event.target as HTMLFormElement)
        const serialized: Record<string, FormDataEntryValue> = {}
        data.forEach((value, key) => serialized[key] = value)
        setGettingRedirect(true)
        try {
            const res = await fetch("/api/landingPages/registerLead", {
                method: "POST",
                headers: {"content-type": "application/json"},
                body: JSON.stringify({
                    ...serialized,
                    pageId: props.pageId,
                    requestedCma: false
                })
            })
            const {status, redirect} = await res.json()
            if (status !== 0) {
                window.alert("An error occurred! Try again later!")
            } else {
                window.location.assign(redirect)
            }
        } catch {
            window.alert("An error occurred! Try again later!")
        }
        setGettingRedirect(false)
    }

    return <>
        <div
        className="absolute z-10 w-screen h-screen bg-overlay flex justify-center items-center"
        >
        <div className="rounded-sm px-1 pb-8 pt-2 bg-gray-100 w-4/5 max-w-screen-sm">
            <div 
                className="text-right"
            >
                <button 
                    className="text-red-500 hover:text-red-700"
                    onClick={props.onClose}
                >
                    <CloseModalIcon/>
                </button>
            </div>

            <div className="flex items-center justify-center">
                <div>
                    <img 
                        src="/mls.png" 
                        alt="MLS logo" 
                        width={"130px"} 
                        height={"130px"}
                    />
                </div>
                
                <div>
                    <img 
                        src="/equal-housing-opportunity.png" 
                        alt="Equal housing opportunity logo"
                        width={"130px"} 
                        height={"130px"}
                    />
                </div>
            </div>

            <div className="text-center mt-3 mb-5 text-2xl text-gray-700">
                {"Search all area properties..."}
            </div>

            <div className="mb-5">
                <form 
                    className="w-4/5 mx-auto"
                    onSubmit={sendSearchPropertyData}
                >
                    <input 
                        type="text"
                        className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                        placeholder="Name"
                        minLength={1}
                        name="name"
                        required
                    /><br/>

                    <input 
                        type="tel"
                        className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                        placeholder="Phone"
                        required
                        name="phone"
                    /><br/>

                    <input 
                        type="email"
                        className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                        placeholder="Email"
                        required
                        name="email"
                    /><br/>

                    <button
                        type="submit"
                        disabled={gettingRedirect}
                        className={"text-xl font-bold py-4 mt-4 w-full rounded text-gray-100 " + (gettingRedirect ? "bg-gray-600" : "bg-blue-600 mb-4 hover:bg-blue-700")}
                    >
                        {gettingRedirect ? "Loading..." : "Start search"}
                    </button><br/>
                </form>
            </div>

            <div>
            </div>

            <div className="text-xs text-gray-600 text-center">
                {"We hate spam and promise to keep your email address safe"}
            </div>
        </div> 
    </div>

    </>
}

type ValutionModalProps = {
    onClose: () => unknown
    firstName: string
    lastName: string
    pageId: string
}

function ValutionModal(props: ValutionModalProps): JSX.Element {
    const [cmaRequested, setCmaRequested] = useState(false)
    
    async function sendValuationData(
        event: FormEvent<HTMLFormElement>
    ) {
        event.preventDefault()
        const data = new FormData(event.target as HTMLFormElement)
        const serialized: Record<string, FormDataEntryValue> = {}
        data.forEach((value, key) => serialized[key] = value)
        const _ = await fetch("/api/landingPages/registerLead", {
            method: "POST",
            headers: {"content-type": "application/json"},
            body: JSON.stringify({
                ...serialized,
                pageId: props.pageId,
                requestedCma: true
            })
        })
        setCmaRequested(true)
    }

    return <div
        className="absolute z-10 w-screen h-screen bg-overlay flex justify-center items-center"
    >
        <div className="rounded-sm px-1 pb-8 pt-2 bg-gray-100 w-4/5 max-w-screen-sm">
            <div 
                className="text-right"
            >
                <button 
                    className="text-red-500 hover:text-red-700"
                    onClick={props.onClose}
                >
                    <CloseModalIcon/>
                </button>
            </div>

            {cmaRequested ? <>
                <div className="mb-2 text-lg">
                    {`${props.firstName} ${props.lastName} will reach out to you shortly! Thank you!`}
                </div>
            </> : <>
                <div className="mt-3 mb-5 text-2xl text-gray-700 text-center">
                    {"Calculate what my home is worth"}
                </div>

                <div className="mb-5">
                    <form 
                        className="w-4/5 mx-auto"
                        onSubmit={sendValuationData}
                    >
                        <input 
                            type="text"
                            className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                            placeholder="Name"
                            minLength={1}
                            name="name"
                            required
                        /><br/>

                        <input 
                            type="tel"
                            className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                            placeholder="Phone"
                            required
                            name="phone"
                        /><br/>

                        <input 
                            type="email"
                            className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                            placeholder="Email"
                            required
                            name="email"
                        /><br/>

                        <input 
                            type="text"
                            className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                            placeholder="Address"
                            minLength={1}
                            name="address"
                            required
                        /><br/>

                        <textarea 
                            className="mb-3 w-full px-3 py-1 rounded border border-solid border-gray-500" 
                            placeholder="Upgrades Made to the Property (If Any) (eg. pool, granite countertops)"
                            name="details"
                            rows={3}
                        /><br/>

                        <button
                            type="submit"
                            className="text-xl font-bold py-4 mt-4 w-full rounded text-gray-100 bg-green-600 mb-4 hover:bg-green-700"
                        >
                            {"Get my valuation"}
                        </button><br/>
                    </form>
                </div>

                <div>
            </div>
            </>}
            

            <div className="text-xs text-gray-600 text-center">
                {"We hate spam and promise to keep your email address safe"}
            </div>
        </div> 
    </div>
}